import React from "react";

import { RouteProps as ReactRouteProps, Route, Routes } from "react-router-dom";

import AccountDetails from "@components/account_profile/AccountDetails";
import AccountRelationships from "@components/account_profile/AccountRelationships";
import CurrentRoyalties from "@components/account_profile/CurrentRoyalties";
import PastResaleReportsComponent from "@components/account_profile/PastResaleReports";
import PastRoyalties from "@components/account_profile/PastRoyalties";
import PendingPaymentsComponent from "@components/account_profile/PendingPayments";
import UnderReviewResalesComponent from "@components/account_profile/UnderReviewResales";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import { AuthenticationContext, useAuthenticationContext } from "@context/authentication";
import { ExternalSignIn } from "@pages/SignIn/External";
import { ForgotPassword } from "@pages/SignIn/ForgotPassword/ForgotPassword";
import { InternalSignIn } from "@pages/SignIn/Internal";
import { SignOut } from "@pages/SignOut";
import { AccountProfile } from "@pages/dashboard/AccountProfile";
import { Accounts } from "@pages/dashboard/Accounts";
import { Dashboard } from "@pages/dashboard/Dashboard";
import { HoldPeriodResale } from "@pages/dashboard/HoldPeriodResale";
import { PastPayments } from "@pages/dashboard/PastPayments";
import { PastReports } from "@pages/dashboard/PastReports";
import { PastResaleReports } from "@pages/dashboard/PastResaleReports";
import { PendingPayments } from "@pages/dashboard/PendingPayments";
import { RecentReports } from "@pages/dashboard/RecentReports";
import { ReportNewResale } from "@pages/dashboard/ReportNewResale";
import { ReportsToReview } from "@pages/dashboard/ReportsToReview";
import { ResaleCsvSubmission } from "@pages/dashboard/ResaleCsvSubmission";
import { ResaleSubmission } from "@pages/dashboard/ResaleSubmission";
import { SubmitAllResales } from "@pages/dashboard/SubmitAllResales";
import { UnderReviewResales } from "@pages/dashboard/UnderReviewResales";
import { UpcomingRoyalties } from "@pages/dashboard/UpcomingRoyalties";
import ContactUs from "@pages/public/ContactUs";
import { Home } from "@pages/public/Home";
import { HowToUseTemplate } from "@pages/public/HowToUseTemplate";
import AC from "@pages/public/MoreInfo/AC";
import AMP from "@pages/public/MoreInfo/AMP";
import Artist from "@pages/public/MoreInfo/Artist";
import { NoticeOfResale } from "@pages/public/NoticeOfResale";
import { RegisterSubmission } from "@pages/register/RegisterSubmission";
import { RegisterSuccess } from "@pages/register/RegisterSuccess";
import { ResetPassword } from "@pages/register/ResetPassword/ResetPassword";
import { AllRoles, ResaleSubmissionRoles, Role } from "@roles";

import { ROUTES } from "./routes";

type Permissions = {
  requiredGroups?: Role[];
  redirectOnUnAuth?: boolean;
};

type RouteProps = ReactRouteProps & {
  permissions?: Permissions;
  auth?: AuthenticationContext;
};

const createRoute = (props: RouteProps): React.ReactElement => {
  const { permissions, element, auth } = props;

  const potentiallyProtectedElement = permissions ? (
    <ProtectedComponent
      requiredGroups={permissions.requiredGroups || []}
      redirectOnUnauth={permissions.redirectOnUnAuth || false}
      auth={auth}
    >
      {element}
    </ProtectedComponent>
  ) : (
    element
  );

  return <Route {...props} element={potentiallyProtectedElement} />;
};

export const App = () => {
  // Sometimes a change in auth needs to recreate the route which is why we need ot pass auth as props to createRoute -> which will recompute calculation of potentiallyProtectedElement when auth context changes.
  const { auth } = useAuthenticationContext();

  return (
    <Routes>
      {createRoute({ auth, path: ROUTES.Root.path, element: <Home /> })}
      {createRoute({ auth, path: ROUTES.ContactUs.path, element: <ContactUs /> })}
      {createRoute({ auth, path: ROUTES.ExternalSignIn.path, element: <ExternalSignIn /> })}
      {createRoute({ auth, path: ROUTES.ExternalRegister.path, element: <ExternalSignIn /> })}
      {createRoute({ auth, path: ROUTES.InternalSignIn.path, element: <InternalSignIn /> })}
      {createRoute({ auth, path: ROUTES.SignOut.path, element: <SignOut /> })}
      {createRoute({ auth, path: ROUTES.MoreInfo.Artist.path, element: <Artist /> })}
      {createRoute({ auth, path: ROUTES.MoreInfo.AMP.path, element: <AMP /> })}
      {createRoute({ auth, path: ROUTES.MoreInfo.AC.path, element: <AC /> })}
      {createRoute({ auth, path: ROUTES.ResetPassword.path, element: <ResetPassword /> })}
      {createRoute({ auth, path: ROUTES.ForgotPassword.path, element: <ForgotPassword /> })}
      {createRoute({ auth, path: ROUTES.NoticeOfResale.path, element: <NoticeOfResale /> })}
      {createRoute({ auth, path: ROUTES.HowToUseTemplate.path, element: <HowToUseTemplate /> })}
      {createRoute({
        auth,
        path: ROUTES.ResaleSubmission.path + "/*",
        element: <ResaleSubmission />,
        permissions: {
          requiredGroups: ResaleSubmissionRoles,
          redirectOnUnAuth: true,
        },
      })}
      {createRoute({
        auth,
        path: ROUTES.SubmitAllResales.path,
        element: <SubmitAllResales />,
        permissions: {
          requiredGroups: ResaleSubmissionRoles,
          redirectOnUnAuth: true,
        },
      })}
      {createRoute({
        auth,
        path: ROUTES.ResaleCsvSubmission.path + "/*",
        element: <ResaleCsvSubmission />,
        permissions: {
          requiredGroups: ResaleSubmissionRoles,
          redirectOnUnAuth: true,
        },
      })}
      {createRoute({
        auth,
        path: ROUTES.Profile.path,
        element: <AccountProfile />,
        permissions: {
          requiredGroups: AllRoles,
          redirectOnUnAuth: true,
        },
        children: (
          <>
            {createRoute({ auth, index: true, element: <AccountDetails /> })}
            {createRoute({
              auth,
              path: ROUTES.Profile.$.AccountRelationships.relativePath,
              element: <AccountRelationships />,
              permissions: {
                requiredGroups: [Role.VisualArtistAdmin, Role.MemberServices, Role.RoyaltyRecipient],
              },
            })}
            {createRoute({ auth, path: ROUTES.Profile.$.CurrentRoyalties.relativePath, element: <CurrentRoyalties /> })}
            {createRoute({ auth, path: ROUTES.Profile.$.PastRoyalties.relativePath, element: <PastRoyalties /> })}
          </>
        ),
      })}
      {createRoute({
        auth,
        path: ROUTES.RegisterSubmission.path + "/*",
        element: <RegisterSubmission path={ROUTES.RegisterSubmission} />,
      })}
      {createRoute({
        auth,
        path: ROUTES.Dashboard.NewAccounts.path + "/*",
        element: <RegisterSubmission path={ROUTES.Dashboard.NewAccounts} />,
        permissions: {
          requiredGroups: [Role.VisualArtistAdmin, Role.MemberServices, Role.Data],
          redirectOnUnAuth: true,
        },
      })}
      {createRoute({ auth, path: ROUTES.RegisterSubmission.RegisterSuccess.path, element: <RegisterSuccess /> })}
      {createRoute({ auth, path: ROUTES.Dashboard.NewAccounts.RegisterSuccess.path, element: <RegisterSuccess /> })}
      {createRoute({
        auth,
        path: ROUTES.Dashboard.path,
        element: <Dashboard />,
        permissions: {
          requiredGroups: AllRoles,
          redirectOnUnAuth: true,
        },
        children: (
          <>
            {createRoute({ auth, path: ROUTES.Dashboard.PastResales.path })}
            {createRoute({ auth, path: ROUTES.Dashboard.HelpCentre.path })}
            {createRoute({ auth, path: ROUTES.Dashboard.Settings.path })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.Accounts.path,
              element: <Accounts />,
              permissions: {
                requiredGroups: [Role.VisualArtistAdmin, Role.MemberServices, Role.Data],
                redirectOnUnAuth: true,
              },
            })}
            {createRoute({ auth, path: ROUTES.Dashboard.CurrentRoyalties.path })}
            {createRoute({ auth, path: ROUTES.Dashboard.PastRoyalties.path })}
            {createRoute({
              auth,
              element: <ReportNewResale />,
              permissions: { requiredGroups: ResaleSubmissionRoles },
              path: ROUTES.Dashboard.ReportNewResale.path,
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.PastResales.path,
              permissions: { requiredGroups: [Role.RoyaltyRecipient], redirectOnUnAuth: true },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.PendingPayments.path,
              element: <PendingPayments />,
              permissions: { requiredGroups: [Role.ResaleReporter], redirectOnUnAuth: true },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.UnderReview.path,
              element: <UnderReviewResales />,
              permissions: { requiredGroups: [Role.ResaleReporter], redirectOnUnAuth: true },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.ReportsToReview.path,
              element: <ReportsToReview />,
              permissions: { requiredGroups: [Role.VisualArtistAdmin, Role.Data], redirectOnUnAuth: true },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.HoldPeriod.path,
              element: <HoldPeriodResale />,
              permissions: {
                requiredGroups: [Role.MemberServices, Role.VisualArtistAdmin, Role.Data],
                redirectOnUnAuth: true,
              },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.PastReports.path,
              element: <PastReports />,
              permissions: {
                requiredGroups: [Role.MemberServices, Role.VisualArtistAdmin, Role.Data],
                redirectOnUnAuth: true,
              },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.PastResaleReports.path,
              element: <PastResaleReports />,
              permissions: {
                requiredGroups: [Role.ResaleReporter],
                redirectOnUnAuth: true,
              },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.RecentReports.path,
              element: <RecentReports />,
              permissions: {
                requiredGroups: [Role.Data, Role.VisualArtistAdmin],
                redirectOnUnAuth: true,
              },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.UpcomingRoyalties.path,
              element: <UpcomingRoyalties />,
              permissions: {
                requiredGroups: [Role.RoyaltyRecipient],
              },
            })}
            {createRoute({
              auth,
              path: ROUTES.Dashboard.PastPayments.path,
              element: <PastPayments />,
              permissions: {
                requiredGroups: [Role.RoyaltyRecipient],
              },
            })}
            ,
            {createRoute({
              auth,
              path: ROUTES.Dashboard.Profile.path,
              element: <AccountProfile />,
              permissions: {
                requiredGroups: AllRoles,
                redirectOnUnAuth: true,
              },
              children: (
                <>
                  {createRoute({ auth, index: true, element: <AccountDetails /> })}
                  {createRoute({
                    auth,
                    path: ROUTES.Dashboard.Profile.$.AccountRelationships.relativePath,
                    element: <AccountRelationships />,
                    permissions: {
                      requiredGroups: [Role.VisualArtistAdmin, Role.MemberServices, Role.RoyaltyRecipient, Role.Data],
                    },
                  })}
                  {createRoute({
                    auth,
                    path: ROUTES.Dashboard.Profile.$.CurrentRoyalties.relativePath,
                    element: <CurrentRoyalties />,
                  })}
                  {createRoute({
                    auth,
                    path: ROUTES.Dashboard.Profile.$.PastRoyalties.relativePath,
                    element: <PastRoyalties />,
                  })}
                  {createRoute({
                    auth,
                    path: ROUTES.Dashboard.Profile.$.UnderReview.relativePath,
                    element: <UnderReviewResalesComponent />,
                  })}
                  {createRoute({
                    auth,
                    path: ROUTES.Dashboard.Profile.$.PendingPayments.relativePath,
                    element: <PendingPaymentsComponent />,
                  })}
                  {createRoute({
                    auth,
                    path: ROUTES.Dashboard.Profile.$.PastResaleReports.relativePath,
                    element: <PastResaleReportsComponent />,
                  })}
                </>
              ),
            })}
          </>
        ),
      })}
    </Routes>
  );
};
